import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API, { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Mail, Send, Plus, Edit, Trash2, Play, BarChart3, Clock,
    CheckCircle, XCircle, Sparkles, FileText
} from 'lucide-react';

export default function CRMPage() {
    const { user } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [emailLog, setEmailLog] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadData = useCallback(async () => {
        try {
            const [cRes, lRes, sRes] = await Promise.all([
                API.get('/crm/campaigns'),
                API.get('/crm/email-log'),
                API.get('/crm/stats'),
            ]);
            setCampaigns(cRes.data);
            setEmailLog(lRes.data);
            setStats(sRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="crm-page">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']">CRM Automation</h1>
                        <p className="text-sm text-zinc-500 mt-1">Email campaigns, drip sequences, and client communication</p>
                    </div>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <StatCard label="Total Campaigns" value={stats.total_campaigns} />
                        <StatCard label="Active Campaigns" value={stats.active_campaigns} />
                        <StatCard label="Total Emails Sent" value={stats.total_emails_sent} />
                        <StatCard label="Campaign Sends" value={stats.campaign_sends} />
                    </div>
                )}

                <Tabs defaultValue="campaigns" className="space-y-6">
                    <TabsList className="bg-white border border-zinc-200 shadow-sm rounded-xl p-1">
                        <TabsTrigger value="campaigns" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="crm-tab-campaigns">
                            <FileText className="w-4 h-4 mr-1" /> Campaigns ({campaigns.length})
                        </TabsTrigger>
                        <TabsTrigger value="send" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="crm-tab-send">
                            <Send className="w-4 h-4 mr-1" /> Send Email
                        </TabsTrigger>
                        <TabsTrigger value="log" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="crm-tab-log">
                            <Clock className="w-4 h-4 mr-1" /> Email Log ({emailLog.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="campaigns">
                        <CampaignsTab campaigns={campaigns} onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="send">
                        <SendEmailTab onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="log">
                        <EmailLogTab logs={emailLog} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function StatCard({ label, value }) {
    return (
        <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
            <div className="text-xs text-zinc-500 mb-1">{label}</div>
            <div className="text-2xl font-semibold text-zinc-900 font-['Outfit']">{value}</div>
        </div>
    );
}

function CampaignsTab({ campaigns, onRefresh }) {
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ name: '', trigger: 'custom', subject: '', body_html: '', delay_hours: 0 });
    const [error, setError] = useState('');
    const triggers = ['enrollment', 'dispute_update', 'milestone', 'payment_reminder', 'inactive', 'custom'];
    const triggerColors = {
        enrollment: 'bg-emerald-100 text-emerald-700', dispute_update: 'bg-blue-100 text-blue-700',
        milestone: 'bg-amber-100 text-amber-700', payment_reminder: 'bg-red-100 text-red-700',
        inactive: 'bg-zinc-100 text-zinc-600', custom: 'bg-purple-100 text-purple-700',
    };

    const addCampaign = async () => {
        if (!form.name || !form.subject) { setError('Name and subject required'); return; }
        setError('');
        try {
            await API.post('/crm/campaigns', { ...form, enabled: true });
            setShowAdd(false);
            setForm({ name: '', trigger: 'custom', subject: '', body_html: '', delay_hours: 0 });
            onRefresh();
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    };

    const toggleCampaign = async (id, enabled) => {
        try { await API.put(`/crm/campaigns/${id}`, { enabled: !enabled }); onRefresh(); }
        catch (err) { console.error(err); }
    };

    const deleteCampaign = async (id) => {
        try { await API.delete(`/crm/campaigns/${id}`); onRefresh(); }
        catch (err) { console.error(err); }
    };

    const testTrigger = async (trigger) => {
        try {
            await API.post(`/crm/trigger/${trigger}`, {
                recipient_email: 'test@visionairerealty.com',
                variables: { client_name: 'Test User', program_tier: 'Accelerated', target_score: '640', dashboard_url: '#', amount: '119', payment_url: '#' }
            });
            onRefresh();
        } catch (err) { console.error(err); }
    };

    return (
        <div className="space-y-4" data-testid="campaigns-tab">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Drip Campaigns</h3>
                <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="add-campaign-btn">
                    <Plus className="w-3 h-3 mr-1" /> New Campaign
                </Button>
            </div>

            {showAdd && (
                <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm space-y-3" data-testid="add-campaign-form">
                    {error && <div className="text-red-600 text-xs">{error}</div>}
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Campaign Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="border-zinc-200 h-8 text-sm" data-testid="campaign-name-input" /></div>
                        <div><Label className="text-xs">Trigger</Label>
                            <select value={form.trigger} onChange={e => setForm(f => ({ ...f, trigger: e.target.value }))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white">
                                {triggers.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                            </select>
                        </div>
                        <div className="col-span-2"><Label className="text-xs">Subject Line</Label><Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="border-zinc-200 h-8 text-sm" data-testid="campaign-subject-input" /></div>
                        <div className="col-span-2"><Label className="text-xs">Email Body (HTML)</Label>
                            <Textarea value={form.body_html} onChange={e => setForm(f => ({ ...f, body_html: e.target.value }))} className="border-zinc-200 text-sm min-h-24 font-mono" placeholder="<p>Hi {{client_name}},</p>" data-testid="campaign-body-input" />
                        </div>
                        <div><Label className="text-xs">Delay (hours after trigger)</Label><Input type="number" value={form.delay_hours} onChange={e => setForm(f => ({ ...f, delay_hours: parseInt(e.target.value) || 0 }))} className="border-zinc-200 h-8 text-sm" /></div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={addCampaign} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">Create</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowAdd(false)} className="text-xs border-zinc-200">Cancel</Button>
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {campaigns.map(c => (
                    <div key={c.id} className={`bg-white rounded-xl border p-5 shadow-sm ${c.enabled ? 'border-zinc-200' : 'border-zinc-100 opacity-60'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-zinc-900">{c.name}</span>
                                <Badge className={triggerColors[c.trigger] || 'bg-zinc-100 text-zinc-600'}>{c.trigger.replace('_', ' ')}</Badge>
                                {c.delay_hours > 0 && <Badge className="bg-zinc-100 text-zinc-500 text-xs"><Clock className="w-2.5 h-2.5 mr-0.5" />{c.delay_hours}h delay</Badge>}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-zinc-400">{c.sent_count} sent</span>
                                <Button size="sm" variant="ghost" onClick={() => toggleCampaign(c.id, c.enabled)} className="text-xs h-6 px-2" data-testid={`toggle-campaign-${c.id}`}>
                                    {c.enabled ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-zinc-400" />}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => testTrigger(c.trigger)} className="text-xs h-6 px-2 text-blue-500" title="Test trigger">
                                    <Play className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteCampaign(c.id)} className="text-xs h-6 px-2 text-red-400">
                                    <Trash2 className="w-3 h-3" />
                                </Button>
                            </div>
                        </div>
                        <div className="text-xs text-zinc-500">Subject: {c.subject}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SendEmailTab({ onRefresh }) {
    const [form, setForm] = useState({ recipient_email: '', subject: '', body_html: '' });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const sendEmail = async () => {
        if (!form.recipient_email || !form.subject) { setError('Recipient and subject required'); return; }
        setLoading(true); setError(''); setSuccess(false);
        try {
            await API.post('/crm/send-email', form);
            setSuccess(true);
            setForm({ recipient_email: '', subject: '', body_html: '' });
            onRefresh();
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    return (
        <div className="space-y-4" data-testid="send-email-tab">
            <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Send Manual Email</h3>
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm space-y-4">
                {error && <div className="p-2 rounded bg-red-50 border border-red-200 text-red-600 text-xs">{error}</div>}
                {success && <div className="p-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-600 text-xs">Email sent successfully!</div>}
                <div><Label className="text-sm text-zinc-600">Recipient Email</Label>
                    <Input value={form.recipient_email} onChange={e => setForm(f => ({ ...f, recipient_email: e.target.value }))} placeholder="client@example.com" className="border-zinc-200 mt-1" data-testid="manual-email-recipient" />
                </div>
                <div><Label className="text-sm text-zinc-600">Subject</Label>
                    <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="border-zinc-200 mt-1" data-testid="manual-email-subject" />
                </div>
                <div><Label className="text-sm text-zinc-600">Body (HTML)</Label>
                    <Textarea value={form.body_html} onChange={e => setForm(f => ({ ...f, body_html: e.target.value }))} className="border-zinc-200 mt-1 min-h-32 font-mono text-sm" placeholder="<p>Hello,</p>" data-testid="manual-email-body" />
                </div>
                <Button onClick={sendEmail} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="send-email-btn">
                    <Send className="w-4 h-4 mr-2" /> {loading ? 'Sending...' : 'Send Email'}
                </Button>
            </div>
        </div>
    );
}

function EmailLogTab({ logs }) {
    return (
        <div className="space-y-4" data-testid="email-log-tab">
            <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Email Log</h3>
            {logs.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                    <Mail className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                    <p className="text-sm text-zinc-400">No emails sent yet.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {logs.map(l => (
                        <div key={l.id} className="bg-white rounded-lg border border-zinc-200 p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${l.trigger === 'manual' ? 'bg-purple-100' : 'bg-emerald-100'}`}>
                                    <Mail className={`w-4 h-4 ${l.trigger === 'manual' ? 'text-purple-600' : 'text-emerald-600'}`} />
                                </div>
                                <div>
                                    <div className="text-sm text-zinc-900">{l.recipient}</div>
                                    <div className="text-xs text-zinc-400">{l.trigger} | {l.campaigns_sent} campaign(s)</div>
                                </div>
                            </div>
                            <span className="text-xs text-zinc-400">{new Date(l.created_at).toLocaleString()}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
